import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { groupPermissionsByCategory } from "@/components/ui/permissions/groupPermissions";
import type { PermissionRow } from "@/stores/admin/models";

/** A role that grants a given permission, as shown in the reference modal. */
export interface GrantingRole {
  code: string;
  name: string;
  deprecated: boolean;
}

interface PermissionsReferenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full permission catalogue, straight from the DB `permissions` table. */
  permissions: PermissionRow[];
  /** Maps a permission code to the roles that currently grant it. */
  rolesByPermission: Map<string, GrantingRole[]>;
}

/**
 * Read-only reference for the whole permission catalogue. Lists every
 * permission grouped by category — code, name, plain-language description,
 * scope, and which roles grant it — so an admin can understand what each
 * permission does before wiring it into a role. Editing happens in the
 * separate PermissionsMultiSelectDialog.
 */
export function PermissionsReferenceDialog({
  open,
  onOpenChange,
  permissions,
  rolesByPermission,
}: PermissionsReferenceDialogProps) {
  const [search, setSearch] = useState("");

  // Reset the search each time the dialog opens.
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const grouped = useMemo(
    () => groupPermissionsByCategory(permissions, search),
    [permissions, search]
  );

  const totalShown = grouped.reduce((sum, g) => sum + g.permissions.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-[680px]">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>Permissions reference</DialogTitle>
          <DialogDescription>
            What each permission allows. This list is read-only — to change which
            permissions a role grants, click the permission count in that role&apos;s row.
          </DialogDescription>
        </DialogHeader>

        <div className="border-b px-6 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code, name, or description…"
              className="pl-9"
              autoFocus
            />
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {totalShown} shown · {permissions.length} total
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="px-6 py-4">
            {grouped.length === 0 ? (
              <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                No permissions match your search.
              </div>
            ) : (
              grouped.map(({ category, permissions: catPerms }, idx) => (
                <div key={category} className={cn(idx > 0 && "mt-4")}>
                  <div className="sticky top-0 z-10 -mx-6 mb-2 flex items-center gap-2 bg-background/95 px-6 py-1 backdrop-blur">
                    <Badge variant="secondary" className="font-mono uppercase">
                      {category}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {catPerms.length} permission{catPerms.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {catPerms.map((p) => {
                      const roles = rolesByPermission.get(p.code) ?? [];
                      return (
                        <div
                          key={p.code}
                          className="rounded-md border border-border bg-card p-3"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                              {p.code}
                            </code>
                            <Badge variant="outline" className="text-xs font-normal">
                              {p.requires_context ? "Contextual" : "Org-wide"}
                            </Badge>
                          </div>
                          <div className="mt-1 text-sm font-medium leading-tight">{p.name}</div>
                          {p.description ? (
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {p.description}
                            </div>
                          ) : (
                            <div className="mt-0.5 text-xs italic text-muted-foreground/70">
                              No description.
                            </div>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-1">
                            <span className="mr-1 text-xs text-muted-foreground">
                              Granted by:
                            </span>
                            {roles.length === 0 ? (
                              <span className="text-xs italic text-muted-foreground/70">
                                no roles
                              </span>
                            ) : (
                              roles.map((r) => (
                                <Badge
                                  key={r.code}
                                  variant="secondary"
                                  className={cn(
                                    "text-xs font-normal",
                                    r.deprecated && "opacity-50"
                                  )}
                                  title={r.deprecated ? `${r.name} (deprecated role)` : r.name}
                                >
                                  {r.name}
                                </Badge>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {idx < grouped.length - 1 ? <Separator className="mt-4" /> : null}
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
